import { createFileRoute, Navigate } from '@tanstack/react-router';
import { z } from 'zod';

import { useAbility } from '@features/authorization';
import {
  crmBtProgramLogDefaultFlowDbValue,
  CrmTrainingProgramLogProfile,
  CrmTrainingProgramLogSkeleton,
} from '@features/crm-training-program-logs';
import {
  CrmCustomerRecordFullPageAccessGate,
  firstAccessibleCrmCustomerRecordArea,
} from '@widgets/crm-customer-record-shell';

export const crmTrainingProgramLogSearchSchema = z.object({
  edit: z.boolean().optional(),
  delete: z.boolean().optional(),
  trainingQ: z.string().max(200).optional(),
  trainingDateStartedFrom: z.iso.date().optional(),
  trainingDateStartedTo: z.iso.date().optional(),
  trainingCursor: z.string().trim().min(1).max(4096).optional(),
  trainingCursorHistory: z.array(z.string().trim().min(1).max(4096)).max(100).optional(),
  trainingFlow: z.string().trim().min(1).max(100).default(crmBtProgramLogDefaultFlowDbValue),
  trainingProgramIds: z.array(z.uuid()).optional(),
  trainingStatus: z.string().trim().min(1).max(120).optional(),
});

export const Route = createFileRoute(
  '/$locale/_authenticated/crm/user-profiles/$userProfileId/training/$programLogId'
)({
  component: CrmCustomerTrainingProgramLogRoute,
  validateSearch: crmTrainingProgramLogSearchSchema,
});

function CrmCustomerTrainingProgramLogRoute() {
  const navigate = Route.useNavigate();
  const { userProfileId: customerId, locale, programLogId } = Route.useParams();
  const search = Route.useSearch();
  const ability = useAbility();

  // The client hint mirrors the Program Log resource ability; the backend still checks its target.
  if (!ability.can('read', 'CrmCustomerProgramLog'))
    return <Navigate to="/$locale/unauthorized" params={{ locale }} replace />;

  const handleBack = () => {
    const listSearch = { ...search };
    delete listSearch.edit;
    delete listSearch.delete;
    void navigate({
      to: '/$locale/crm/user-profiles/$userProfileId/training',
      params: { userProfileId: customerId, locale },
      search: listSearch,
    });
  };

  return (
    <main className="min-w-0 p-4 sm:p-6">
      <CrmCustomerRecordFullPageAccessGate
        customerId={customerId}
        requiredArea="training"
        loadingFallback={<CrmTrainingProgramLogSkeleton onBack={handleBack} />}
        onAccessUnavailable={(allowedAreas) => {
          const fallback = firstAccessibleCrmCustomerRecordArea(allowedAreas);
          return (
            <Navigate
              to={fallback?.route ?? '/$locale/unauthorized'}
              params={{ userProfileId: customerId, locale }}
              replace
              resetScroll={false}
              search={search}
            />
          );
        }}
      >
        {(viewerAccessScope) => (
          <CrmTrainingProgramLogProfile
            id={programLogId}
            customerId={customerId}
            initialEdit={search.edit === true && ability.can('update', 'CrmCustomerProgramLog')}
            initialDelete={search.delete === true}
            onDeleteAccepted={(operationId) => {
              const acceptedSearch = { ...search };
              delete acceptedSearch.edit;
              delete acceptedSearch.delete;
              void navigate({
                to: '/$locale/crm/user-profiles/$userProfileId/training',
                params: { userProfileId: customerId, locale },
                search: {
                  ...acceptedSearch,
                  trainingDeletionOperationId: operationId,
                  trainingDeletedLogId: programLogId,
                  trainingRemovalOperationId: undefined,
                  trainingRemovedProgramId: undefined,
                },
                replace: true,
              });
            }}
            onRemoveProgramAccepted={(operationId, trainingProgramId) => {
              const acceptedSearch = { ...search };
              delete acceptedSearch.edit;
              delete acceptedSearch.delete;
              void navigate({
                to: '/$locale/crm/user-profiles/$userProfileId/training',
                params: { userProfileId: customerId, locale },
                search: {
                  ...acceptedSearch,
                  trainingRemovalOperationId: operationId,
                  trainingRemovedProgramId: trainingProgramId,
                  trainingDeletionOperationId: undefined,
                  trainingDeletedLogId: undefined,
                },
                replace: true,
              });
            }}
            locale={locale}
            viewerAccessScope={viewerAccessScope}
            onCancelEdit={() =>
              void navigate({
                replace: true,
                search: (current) => ({ ...current, edit: undefined }),
              })
            }
            onBack={handleBack}
          />
        )}
      </CrmCustomerRecordFullPageAccessGate>
    </main>
  );
}
